import React, { useEffect, lazy, Suspense, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams, useLocation } from "react-router-dom";
import { getProject } from "redux/crm/projectSlice";
import { useDetailRecord } from "helpers/detailRequest";
import { DetailRecordFallback } from "components/common";
import ProjectDetailHeader from "components/app/crm/project/detail/ProjectDetailHeader";
import SimpleBarReact from 'simplebar-react';
import { formatDate, formatNumber } from "helpers/utils";
import useBreadcrumb from "hooks/useBreadcrumb";
import {
  MdNewReleases, MdBusiness, MdOpenInNew, MdLocalActivity,
  MdPerson, MdList, MdAttachMoney, MdFlightTakeoff, MdMilitaryTech,
  MdInsertDriveFile, MdForum, MdHistory, MdSchedule, MdTrendingUp,
  MdCalendarToday,
} from "react-icons/md";
import { FaAward } from "react-icons/fa";
import PropTypes from 'prop-types';

const LazyProjectDetailWaiver       = lazy(() => import("components/app/crm/project/detail/ProjectDetailWaiver"));
const LazyProjectDetailTravelBooking = lazy(() => import("components/app/crm/project/detail/ProjectDetailTravelBooking"));
const LazyProjectDetailHistoryTracking = lazy(() => import("components/app/crm/project/detail/ProjectHistoryTracking"));
const LazyProjectDetailOverview      = lazy(() => import("components/app/crm/project/detail/ProjectDetailOverview"));
const LazyProjectDetailDocument      = lazy(() => import("components/app/crm/project/detail/ProjectDetailDocument"));
const LazyWarrantyService            = lazy(() => import("components/app/crm/project/detail/WarrantyService"));
const LazyPMD                        = lazy(() => import("components/app/crm/project/detail/Pmd"));
const LazyProjectDetailInteraction   = lazy(() => import("components/app/crm/project/detail/ProjectInteraction"));
const LazyProjectDetailActivity      = lazy(() => import("components/app/crm/project/detail/ProjectDetailActivity"));
const LazyProjectDetailDiscussion    = lazy(() => import("components/app/crm/project/detail/ProjectDetailDiscussion")); 

// ── Loading fallback ──────────────────────────────────────────────────────────
const TabLoadingFallback = ({ message = "Loading..." }) => (
  <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16 tw-gap-3">
    <div className="tw-w-8 tw-h-8 tw-border-4 tw-border-primary-200 tw-border-t-primary-600 tw-rounded-xl tw-animate-spin" />
    <span className="tw-text-sm tw-text-slate-400">{message}</span>
  </div>
);
TabLoadingFallback.propTypes = { message: PropTypes.string };

// ── Tab definitions ───────────────────────────────────────────────────────────
const TABS = [
  { key: 'project-overview',       hash: '#project-overview',       icon: MdList,            label: 'Detail',         color: 'tw-text-primary-500' },
  { key: 'project-activity',       hash: '#project-activity',       icon: MdLocalActivity,   label: 'Activity',        color: 'tw-text-slate-500' },
  { key: 'project-discussion',     hash: '#project-discussion',     icon: MdForum,           label: 'Discussion',      color: 'tw-text-primary-500' },
  { key: 'project-waiver',         hash: '#project-waiver',         icon: MdAttachMoney,     label: 'Waiver',          color: 'tw-text-emerald-500' },
  { key: 'project-travel-booking', hash: '#project-travel-booking', icon: MdFlightTakeoff,   label: 'Bookings',        color: 'tw-text-sky-500' },
  { key: 'project-pmd',            hash: '#project-pmd',            icon: MdSchedule,        label: 'PMD',             color: 'tw-text-slate-500', permKey: 'allow_view_pmd' },
  { key: 'project-warranty',       hash: '#project-warranty',       icon: MdMilitaryTech,    label: 'Warranty',        color: 'tw-text-amber-500' },
  { key: 'project-documents',      hash: '#project-documents',      icon: MdInsertDriveFile, label: 'Documents',       color: 'tw-text-primary-500' },
  { key: 'project-interaction',    hash: '#project-interaction',    icon: MdForum,           label: 'Interactions',    color: 'tw-text-primary-500' },
  { key: 'project-history-log',    hash: '#project-history-log',    icon: MdHistory,         label: 'History',         color: 'tw-text-slate-500' },
];

const hashToKey = Object.fromEntries(TABS.map(t => [t.hash, t.key]));

// ── ProjectDetailPage ─────────────────────────────────────────────────────────────
const ProjectDetailPage = () => {
  const params   = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  // Only the project in the URL — the persisted store can still hold the one
  // viewed last (e.g. when opened from an email link).
  const { record: project, waiting, error }        = useDetailRecord('crm_project', params.id);
  const warranties                                 = useSelector(state => state.crm_warranty?.allData?.data);
  const interactionTotal                           = useSelector(state => state.crm_project_interaction?.paginate?.total_rows);

  const getInitialTab = useCallback(
    () => hashToKey[location.hash] || 'project-overview',
    [location.hash]
  );

  const [activeTab,   setActiveTab]   = useState(getInitialTab);
  const [visitedTabs, setVisitedTabs] = useState(() => new Set([getInitialTab()]));
  // Bumped by the header's Refresh button to force the active tab to remount
  // (re-running its own data fetch) alongside re-fetching the project itself —
  // each tab owns a separate redux slice that the project fetch doesn't touch.
  const [refreshNonce, setRefreshNonce] = useState(0);

  const projectId = params.id;

  // Breadcrumb: hiển thị tên project (vd "Tet Building 7") thay vì id trên header.
  useBreadcrumb(
    project
      ? [
          { label: 'Projects', to: '/crm/projects/list' },
          {
            label:
              project.name ||
              project.j_number ||
              project.q_number ||
              `Project ${projectId}`
          }
        ]
      : null
  );

  useEffect(() => {
    if (projectId) dispatch(getProject(projectId));
  }, [projectId, dispatch]);

  const handleRefresh = useCallback(() => {
    if (projectId) dispatch(getProject(projectId));
    setRefreshNonce(n => n + 1);
  }, [dispatch, projectId]);

  const handleTabChange = useCallback((key) => {
    React.startTransition?.(() => {
      setActiveTab(key);
      setVisitedTabs(prev => new Set([...prev, key]));
      const hash = TABS.find(t => t.key === key)?.hash;
      if (hash) window.history.replaceState(null, '', `${window.location.pathname}${hash}`);
    }) ?? (() => {
      setActiveTab(key);
      setVisitedTabs(prev => new Set([...prev, key]));
    })();
  }, []);

  useEffect(() => {
    const onHashChange = () => {
      const target = hashToKey[window.location.hash];
      if (target && target !== activeTab) handleTabChange(target);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [activeTab, handleTabChange]);

  const tabCounts = useMemo(() => ({
    'project-waiver':         project?.waivers?.length          || 0,
    'project-travel-booking': project?.job_transactions?.length || 0,
    'project-warranty':       warranties?.length                || 0,
    'project-interaction':    interactionTotal                  || 0,
  }), [project?.waivers, project?.job_transactions, warranties, interactionTotal]);

  const projectData = useMemo(() => {
    if (!project) return {};
    const status  = project?.sales_pipeline;
    const weight  = (project?.final_weight?.est || 0) > 0 ? project.final_weight.est : project?.weight;
    const { allow_view_warranty, allow_view_pmd } = project?.permissions || {};
    const isLost           = status?.group === 'Lost';
    const lostCancelStatus = isLost ? (status?.name || 'Lost') : '';
    // % Success follows the current sales-pipeline stage probability; falls back
    // to the legacy percent_success column when the stage carries no probability.
    const percentSuccess = status?.probability ?? project?.percent_success ?? 0;
    return {
      status, weight, percentSuccess,
      allow_view_warranty, allow_view_pmd,
      hasJobNumber: project?.has_job_number,
      lostCancelStatus,
      showStatusAlert: isLost,
      customer: project?.customer,
    };
  }, [project]);

  const renderTabContent = (tabKey, Component, msg) => {
    if (!visitedTabs.has(tabKey)) return null;
    // `key` includes refreshNonce so a Refresh remounts the tab → it re-fetches.
    return <Suspense key={`${tabKey}-${refreshNonce}`} fallback={<TabLoadingFallback message={msg} />}><Component /></Suspense>;
  };

  const WarrantyTab = useMemo(() => {
    if (!visitedTabs.has('project-warranty')) return null;
    if (projectData.hasJobNumber && projectData.allow_view_warranty) return <Suspense key={`warranty-${refreshNonce}`} fallback={<TabLoadingFallback message="Loading warranty..." />}><LazyWarrantyService /></Suspense>;
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-py-16 tw-gap-3 tw-text-slate-400">
        <FaAward size={48} className="tw-text-amber-300" />
        <p className="tw-text-sm tw-text-center tw-max-w-sm">
          {!projectData.hasJobNumber
            ? "The project must have a job number before you can access warranty."
            : "You do not have permission to view the warranty."}
        </p>
      </div>
    );
  }, [projectData.hasJobNumber, projectData.allow_view_warranty, visitedTabs, refreshNonce]);

  if (!project) {
    return waiting
      ? <TabLoadingFallback message="Loading project details..." />
      : <DetailRecordFallback waiting={false} title="Project not available" error={error} onRetry={handleRefresh} />;
  }

  const visibleTabs = TABS.filter(t => !t.permKey || projectData[t.permKey]);

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <ProjectDetailHeader project={project} onRefresh={handleRefresh} />

      <ProjectSummaryCard project={project} projectData={projectData} />

      {projectData.showStatusAlert && (
        <div className="tw-flex tw-items-start tw-gap-3 tw-px-4 tw-py-3 tw-rounded-lg tw-bg-amber-50 tw-border tw-border-amber-200 tw-text-sm tw-text-amber-800">
          <MdNewReleases size={18} className="tw-shrink-0 tw-mt-0.5" />
          <span>
            The project has been <strong>{projectData.lostCancelStatus}</strong> due to{' '}
            <span className="tw-text-red-600">{project.lost_reason || 'insufficient customer demand.'}</span>
          </span>
        </div>
      )}

      {/* ── Tabs ── */}
      <div className="tw-bg-white tw-rounded-2xl tw-border tw-border-slate-100 tw-shadow-sm">
        {/* Tab bar */}
        <div className="tw-rounded-t-2xl tw-overflow-hidden">
        <SimpleBarReact>
          <div className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-2 tw-border-b tw-border-slate-100 tw-bg-white">
            {visibleTabs.map(tab => {
              const Icon     = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => handleTabChange(tab.key)}
                  className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-xl tw-text-sm tw-font-medium tw-whitespace-nowrap tw-transition-all tw-duration-150 tw-border-0 tw-cursor-pointer ${
                    isActive
                      ? 'tw-bg-primary-600 tw-text-white tw-shadow-sm'
                      : 'tw-bg-transparent tw-text-slate-500 hover:tw-bg-slate-100 hover:tw-text-slate-700'
                  }`}
                >
                  <Icon size={14} className={isActive ? 'tw-text-white' : tab.color} />
                  {tab.label}
                  {tabCounts[tab.key] > 0 && (
                    <span className={`tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-text-[10px] tw-font-bold tw-leading-none ${
                      isActive
                        ? 'tw-bg-white/25 tw-text-white'
                        : 'tw-bg-slate-200 tw-text-slate-600'
                    }`}>
                      {tabCounts[tab.key]}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </SimpleBarReact>
        </div>

        {/* Tab content — no overflow-hidden so react-select dropdowns render correctly */}
        <div className="tw-bg-slate-50 tw-rounded-b-2xl tw-min-h-[400px] tw-p-4">
          {activeTab === 'project-overview'       && renderTabContent('project-overview',       LazyProjectDetailOverview,     'Loading overview...')}
          {activeTab === 'project-activity'       && renderTabContent('project-activity',       LazyProjectDetailActivity,     'Loading activities...')}
          {activeTab === 'project-discussion'     && renderTabContent('project-discussion',     LazyProjectDetailDiscussion,   'Loading discussions...')}
          {activeTab === 'project-waiver'         && renderTabContent('project-waiver',         LazyProjectDetailWaiver,       'Loading waiver details...')}
          {activeTab === 'project-travel-booking' && renderTabContent('project-travel-booking', LazyProjectDetailTravelBooking,'Loading travel bookings...')}
          {activeTab === 'project-pmd'            && renderTabContent('project-pmd',            LazyPMD,                      'Loading PMD data...')}
          {activeTab === 'project-warranty'       && WarrantyTab}
          {activeTab === 'project-documents'      && renderTabContent('project-documents',      LazyProjectDetailDocument,    'Loading documents...')}
          {activeTab === 'project-interaction'    && renderTabContent('project-interaction',    LazyProjectDetailInteraction, 'Loading interactions...')}
          {activeTab === 'project-history-log'   && renderTabContent('project-history-log',   LazyProjectDetailHistoryTracking,  'Loading histories...')}
        </div>
      </div>
    </div>
  );
};

// ── Project Summary Card ──────────────────────────────────────────────────────
const STATUS_THEMES = {
  primary:   { badge: 'tw-bg-primary-100 tw-text-primary-700',   accent: '#4f46e5' },
  success:   { badge: 'tw-bg-emerald-100 tw-text-emerald-700', accent: '#059669' },
  warning:   { badge: 'tw-bg-amber-100 tw-text-amber-700',     accent: '#d97706' },
  danger:    { badge: 'tw-bg-red-100 tw-text-red-600',         accent: '#dc2626' },
  info:      { badge: 'tw-bg-sky-100 tw-text-sky-700',         accent: '#0284c7' },
  secondary: { badge: 'tw-bg-slate-100 tw-text-slate-600',     accent: '#64748b' },
};

const MetaRow = ({ icon: Icon, iconCls, children }) => (
  <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-[11px] tw-text-slate-500">
    <span className={`tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-sm tw-bg-slate-100 tw-shrink-0 ${iconCls || ''}`}>
      <Icon size={10} />
    </span>
    {children}
  </span>
);
MetaRow.propTypes = { icon: PropTypes.elementType.isRequired, iconCls: PropTypes.string, children: PropTypes.node };

const KpiBox = ({ label, accent, children }) => (
  <div className="tw-rounded-lg tw-p-3" style={{ background: `${accent}0d`, border: `1px solid ${accent}22` }}>
    <span className="tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wider" style={{ color: accent }}>
      {label}
    </span>
    <div className="tw-mt-1">{children}</div>
  </div>
);
KpiBox.propTypes = { label: PropTypes.string.isRequired, accent: PropTypes.string.isRequired, children: PropTypes.node };

const ProjectSummaryCard = React.memo(({ project, projectData }) => {
  const theme = STATUS_THEMES[projectData.status?.color] || STATUS_THEMES.secondary;

  return (
    <div className="tw-bg-white tw-rounded-2xl tw-border tw-border-slate-100 tw-shadow-sm tw-overflow-hidden">
      {/* Accent bar — fixed indigo to match app primary */}
      <div className="tw-h-[3px] tw-bg-gradient-to-r tw-from-primary-600 tw-to-primary-400" />

      <div className="tw-p-5">
        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-[2fr_1.5fr_1fr_1fr] tw-gap-x-6 tw-gap-y-5">

          {/* ── Identity ── */}
          <div className="tw-flex tw-flex-col tw-gap-3">
            <div className="tw-flex tw-items-start tw-gap-3">
              <div className="tw-flex tw-items-center tw-justify-center tw-w-10 tw-h-10 tw-rounded-lg tw-shrink-0 tw-text-white tw-shadow-sm tw-bg-primary-600">
                <MdBusiness size={20} />
              </div>
              <div className="tw-min-w-0">
                <h5 className="tw-text-[15px] tw-font-bold tw-text-slate-800 tw-m-0 tw-leading-snug tw-mb-1 tw-truncate">
                  {project?.name}
                </h5>
                <span className={`tw-inline-flex tw-items-center tw-px-2.5 tw-py-0.5 tw-rounded-xl tw-text-xs tw-font-semibold ${theme.badge}`}>
                  {projectData.status?.name}
                </span>
              </div>
            </div>

            <div className="tw-flex tw-flex-col tw-gap-1.5">
              <MetaRow icon={MdPerson}>
                Created by <strong className="tw-text-slate-700 tw-ml-0.5">{project?.created_by}</strong>
                <span className="tw-text-slate-300 tw-mx-0.5">·</span>
                {project?.created_date}
              </MetaRow>
              <MetaRow icon={MdCalendarToday} iconCls="tw-text-primary-400">
                Open: <strong className="tw-text-slate-700 tw-ml-0.5">{formatDate(project?.open_date) || '—'}</strong>
                <span className="tw-text-slate-300 tw-mx-1">|</span>
                Closed: <strong className="tw-text-slate-700 tw-ml-0.5">{formatDate(project?.closed_date) || '—'}</strong>
              </MetaRow>
              <MetaRow icon={MdPerson} iconCls="tw-text-emerald-400">
                Sales incharge: <strong className="tw-text-slate-700 tw-ml-0.5">{project?.sales_incharge || '—'}</strong>
              </MetaRow>
            </div>
          </div>

          {/* ── Customer & numbers ── */}
          <div className="tw-flex tw-flex-col tw-gap-3 tw-border-l tw-border-slate-100 tw-pl-5">
            <div>
              <span className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-slate-400">Customer</span>
              <Link
                to={`/crm/customers/${projectData.customer?.id}`}
                target="_blank"
                className="tw-flex tw-items-center tw-gap-1 tw-text-[13px] tw-font-bold tw-text-primary-600 hover:tw-underline tw-mt-0.5"
              >
                {projectData.customer?.name}
                <MdOpenInNew size={11} className="tw-shrink-0" />
              </Link>
            </div>

            <div className="tw-flex tw-flex-col tw-gap-2">
              <div>
                <span className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-slate-400">Quote number</span>
                <p className="tw-m-0 tw-text-[13px] tw-font-semibold tw-text-primary-600">{project?.q_number || '—'}</p>
              </div>
              <div>
                <span className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-slate-400">P/J number</span>
                <p className="tw-m-0 tw-text-[13px] tw-font-semibold tw-text-primary-600">{project?.j_number || '—'}</p>
              </div>
            </div>

            <div>
              <span className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-slate-400">Project type</span>
              <div className="tw-mt-1">
                <span className={`tw-inline-flex tw-items-center tw-px-2.5 tw-py-0.5 tw-rounded-xl tw-text-xs tw-font-semibold ${project?.project_type === 'hot_roll' ? 'tw-bg-amber-100 tw-text-amber-700' : 'tw-bg-primary-100 tw-text-primary-700'}`}>
                  {project?.project_type === 'hot_roll' ? 'Hot Roll' : 'Built Up'}
                </span>
              </div>
            </div>
          </div>

          {/* ── Amount & % success ── */}
          <div className="tw-flex tw-flex-col tw-gap-3 tw-border-l tw-border-slate-100 tw-pl-5">
            <KpiBox label="Amount" accent="#059669">
              <div className="tw-flex tw-items-baseline tw-gap-0.5">
                <span className="tw-text-[12px] tw-font-bold tw-text-emerald-600">$</span>
                <span className="tw-text-[22px] tw-font-extrabold tw-text-emerald-700 tw-leading-none">
                  {formatNumber(project?.amount)}
                </span>
              </div>
            </KpiBox>

            <div>
              <div className="tw-flex tw-items-center tw-justify-between tw-mb-1.5">
                <span className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-slate-400 tw-flex tw-items-center tw-gap-1">
                  <MdTrendingUp size={11} /> % Success
                </span>
                <span className="tw-text-[12px] tw-font-extrabold" style={{ color: theme.accent }}>
                  {projectData.percentSuccess || 0}%
                </span>
              </div>
              <div className="tw-w-full tw-bg-slate-100 tw-rounded-xl tw-h-2 tw-overflow-hidden">
                <div
                  className="tw-h-2 tw-rounded-xl tw-transition-all tw-duration-500"
                  style={{ width: `${projectData.percentSuccess || 0}%`, background: theme.accent }}
                />
              </div>
            </div>
          </div>

          {/* ── Weight & scales ── */}
          <div className="tw-flex tw-flex-col tw-gap-2.5 tw-border-l tw-border-slate-100 tw-pl-5">
            <KpiBox label="Est. Weight" accent="#4f46e5">
              <div className="tw-flex tw-items-baseline tw-gap-1">
                <span className="tw-text-[22px] tw-font-extrabold tw-text-primary-700 tw-leading-none">
                  {formatNumber(projectData.weight)}
                </span>
                <span className="tw-text-[11px] tw-font-medium tw-text-slate-400">M.T</span>
              </div>
            </KpiBox>

            <KpiBox label="Est. Scales" accent="#64748b">
              <div className="tw-flex tw-items-baseline tw-gap-1">
                <span className="tw-text-[22px] tw-font-extrabold tw-text-slate-700 tw-leading-none">
                  {formatNumber(project?.area)}
                </span>
                <span className="tw-text-[11px] tw-font-medium tw-text-slate-400">m²</span>
              </div>
            </KpiBox>
          </div>

        </div>
      </div>
    </div>
  );
});

ProjectSummaryCard.displayName = 'ProjectSummaryCard';
ProjectSummaryCard.propTypes = {
  project:     PropTypes.object,
  projectData: PropTypes.object,
};

export default ProjectDetailPage;
